/*
 * Copyright Mimic Networks, Inc. 2025.
 */
import { Grid } from 'antd';

import { CountLabel } from '@/components/CountLabel';
import { PageHeader } from '@/components/PageHeader';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Segmented, SegmentedValue } from '@/primitives/Segmented';
import './ConfigsHeader.css';

// eslint-disable-next-line react-refresh/only-export-components
export enum ConfigFilter {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

export type ConfigHeaderProps = {
  configState: ConfigFilter.ACTIVE | ConfigFilter.ARCHIVED;
  setConfigState: (configState: ConfigFilter) => void;
  activeCount: number;
  archivedCount: number;
  activeConfigsLoading: boolean;
  archivedConfigsLoading: boolean;
};

const { useBreakpoint } = Grid;

export function ConfigsHeader({
  setConfigState,
  configState,
  activeCount,
  archivedCount,
  activeConfigsLoading,
  archivedConfigsLoading,
}: ConfigHeaderProps) {
  const { t } = useMimicTranslation('nodeConfigurations');
  const screens = useBreakpoint();

  const onChangeConfigState = (value: SegmentedValue) => {
    setConfigState(value as ConfigFilter);
  };

  return (
    <div className="configs-header">
      <PageHeader title={t('title')} level={2} />
      <div className="configs-segmented-container">
        <Segmented
          dataTestId="config-state-switcher"
          defaultValue={ConfigFilter.ACTIVE}
          value={configState}
          onChange={onChangeConfigState}
          block={!screens.sm}
          options={[
            {
              label: (
                <CountLabel
                  text={t('active')}
                  count={activeCount}
                  loading={activeConfigsLoading}
                  data-testid="segment-active"
                />
              ),
              value: ConfigFilter.ACTIVE,
            },
            {
              label: (
                <CountLabel
                  text={t('archived')}
                  count={archivedCount}
                  loading={archivedConfigsLoading}
                  data-testid="segment-archived"
                />
              ),
              value: ConfigFilter.ARCHIVED,
            },
          ]}
        />
      </div>
    </div>
  );
}
